import livingRoom from 'react-scale/public/static/images/pages/homepage/build-ai/living-room.png';
import { Tab as HeadlessuiTab } from '@headlessui/react';
import cn from 'classnames';
import { useRef, useEffect, useState } from 'react';
import { gsap } from 'react-scale/lib/gsap';
import CTA from 'react-scale/components/elements/cta';
import s from './tabs.module.scss';
import { Tab3D } from './content/3d';
import { TabImage } from './content/image';
import { TabMapping } from './content/mapping';
import { TabText } from './content/text';
import { TabAudio } from './content/audio';

export const TABS = [
  {
    title: '3D',
    content: <Tab3D />
  },
  {
    title: 'Image',
    content: <TabImage url={livingRoom} alt="a living room with furniture" />
  },
  {
    title: 'Mapping',
    content: <TabMapping />
  },
  {
    title: 'Text',
    content: <TabText />
  },
  {
    title: 'Audio',
    content: <TabAudio />
  }
];

export const AnnotationTabs = ({ tabs = [] }) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const cardsRef = useRef<HTMLDivElement[] | HTMLElement[]>([]);
  const markerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (!cardsRef.current || cardsRef.current.length < 1 || !markerRef.current) return;

    const tl = gsap.timeline({ autoRemoveChildren: true, smoothChildTiming: true });

    tl.to(markerRef.current, {
      left: cardsRef.current[selectedTabIndex].offsetLeft,
      width: cardsRef.current[selectedTabIndex].offsetWidth,
      height: cardsRef.current[selectedTabIndex].offsetHeight,
      duration: 0.3,
      ease: 'Power2.inOut'
    });
  }, [selectedTabIndex]);

  return (
    <HeadlessuiTab.Group onChange={index => setSelectedTabIndex(index)}>
      <div className="flex flex-col-reverse items-start lg:flex-row">
        <div className="relative no-scrollbar sm:mx-0 lg:w-[47%] overflow-visible xl:w-auto max-w-full">
          <div className="px-8 mb-8 -mx-8 overflow-x-auto">
            <HeadlessuiTab.List className={s.tabs}>
              <div
                ref={markerRef}
                className="absolute w-20 h-20 bg-black sm:bg-white rounded-full"
              />

              {tabs.map(({ title }, index) => {
                return (
                  <HeadlessuiTab
                    key={index}
                    ref={el => (cardsRef.current = [...cardsRef.current, el])}
                    className={({ selected }) =>
                      cn(
                        'rounded-full py-2 px-4 relative transition-colors duration-300 focus:outline-none',
                        selected ? 'text-white sm:text-black' : 'text-black sm:text-white'
                      )
                    }
                  >
                    {title}
                  </HeadlessuiTab>
                );
              })}
            </HeadlessuiTab.List>
          </div>

          <p className="mb-8 text-sm md:text-base text-black sm:text-white">
            Scale has pioneered in the data labeling industry by combining AI-based techniques with
            human-in-the-loop, delivering labeled data at unprecedented quality, scalability,
            and&nbsp;efficiency.
          </p>
          {/* @ts-ignore: Unreachable code error */}
          <CTA
            cta={{
              buttons: [
                {
                  text: 'Learn More',
                  href: '/data-engine',
                  type: 'primary',
                  variant: 'black-transparent'
                },
                {
                  text: 'Label My Data',
                  href: 'https://dashboard.scale.com/signup?product=rapid&redirect_url=%2Frapid',
                  type: 'secondary'
                }
              ]
            }}
          />
        </div>

        <HeadlessuiTab.Panels className="w-full lg:w-auto">
          {tabs.map(({ content }, index) => {
            return <HeadlessuiTab.Panel key={index}>{content}</HeadlessuiTab.Panel>;
          })}
        </HeadlessuiTab.Panels>
      </div>
    </HeadlessuiTab.Group>
  );
};
